// components/People/addPeopleToNode.tsx
import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

import AddressPickerDialog, { AddressType } from "components/Flow/Controls/AddressPickerDialog";

/**
 * Opens a dialog that lets the user select addresses
 * and adds them to a node field.
 */
export function addAddressesToNode(params: {
  nodeId: number;
  fieldName: string; // e.g. "addresses"
  addressesAPI: { getAll: () => Promise<any[]> };
  nodeAPI: { updateField: (id: number, field: string, value: any) => Promise<any> };
  currentAddresses: any[];
  onAdded?: () => void;
}) {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const CleanupWrapper = () => {
    const [open, setOpen] = useState(true);
    const [selectedAddresses, setSelectedAddresses] = useState<AddressType[]>([]);

    const existingIds = useMemo(() => {
      return params.currentAddresses
        .map((a: any) => (typeof a === "object" ? a?.id : a))
        .filter((id: any): id is number => typeof id === "number");
    }, []);

    const handleClose = () => {
      setOpen(false);
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 200);
    };

    const handleConfirm = async () => {
      if (selectedAddresses.length === 0) return;

      const newIds = selectedAddresses
        .map((a: any) => a?.id)
        .filter((id: any): id is number => typeof id === "number");

      const mergedIds = Array.from(new Set([...existingIds, ...newIds]));

      await params.nodeAPI.updateField(params.nodeId, params.fieldName, mergedIds);

      handleClose();
      params.onAdded?.();
    };

    return (
      <AddressPickerDialog
        open={open}
        title="Select Addresses to Add"
        fetchAddresses={params.addressesAPI.getAll}
        selected={selectedAddresses}
        onSelectedChange={setSelectedAddresses}
        disabledIds={existingIds} // disables already-on-node addresses
        confirmLabel="Add Selected Addresses"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    );
  };

  root.render(<CleanupWrapper />);
}
